<script lang="ts">
import { onMount } from "svelte";
import { fade } from "svelte/transition";
import { quintOut } from "svelte/easing";

import { fetchMajorResearchItems } from "../services/major-research";
import type { MajorProject } from "../structs";

export let id = "";

let data, filtered: MajorProject;


const majorProjectDataFetch = new Promise(async (resolve: (data: MajorProject[]) => void) => {
    const data = await fetchMajorResearchItems();
    resolve(data);
});

onMount(async () => {
    data = await majorProjectDataFetch;
})

// Fetch major project by filtering through array until matching id is found
$: filtered = (data) ? 
        ((data.filter(item => item.id === id).length >= 1) 
        ? data.filter(item => item.id === id)[0] 
        : null) 
    : null;

</script>

{#await majorProjectDataFetch}
    <div class="content">
        <!-- Loading Animation -->
        <div class="loading-cube-grid">
            <div class="sk-cube sk-cube1"></div>
            <div class="sk-cube sk-cube2"></div>
            <div class="sk-cube sk-cube3"></div>
            <div class="sk-cube sk-cube4"></div>
            <div class="sk-cube sk-cube5"></div>
            <div class="sk-cube sk-cube6"></div>
            <div class="sk-cube sk-cube7"></div>
            <div class="sk-cube sk-cube8"></div>
            <div class="sk-cube sk-cube9"></div>
        </div>
    </div>
{:then project} 
    <div in:fade={{ easing: quintOut, duration: 1000 }}>
        {#if filtered}
            <div class="top-background" style="background-image:url({filtered.backgroundImageSrc});">
                <h1 class="page-title">Project: {@html filtered.name}</h1>
            </div>

            <div class="content">
                <section id="major-project-details">

                    <div class="flex-wrapper">
                        <div class="flex-column first">
                            <!-- Project Image -->
                            {#if filtered.projectImageSrc}
                                <img src={filtered.projectImageSrc} class="project-picture" alt="Project">
                            {:else}
                                <img src="/images/research/default.jpg" class="project-picture" alt="Project Default">
                            {/if}

                            <!-- Project Team -->
                            <h3 class="subtitle">Project Team</h3>
                            <ul class="card-list">
                                {#if filtered.team}
                                    {#each filtered.team as person}
                                        <li>{@html person}</li>
                                    {/each}
                                {/if}
                            </ul>
                        </div>

                        <div class="flex-column secondary">
                            <!-- Summary  -->
                            <p>{@html filtered.summary}</p><br>

                            <!-- Project Impact  -->
                            {#if filtered.impact}
                                <h3 class="subtitle">Project Impact</h3>
                                <p>{@html filtered.impact}</p><br>
                            {/if}

                            <!-- Collaborators -->
                            {#if filtered.collaborators}
                                <h3 class="subtitle">Collaborators</h3>
                                <div class="image-container">
                                    {#each filtered.collaborators as src}
                                        <img {src} alt="Collaborator">
                                    {/each}
                                </div>
                            {/if}

                            <!-- Publications -->
                            {#if filtered.publications}
                                {#each filtered.publications as pubItem}
                                    <div class="detail-wrapper">
                                        <h3 class="subtitle">{@html pubItem.category}</h3>
                                        
                                        {#if Array.isArray(pubItem.items)}
                                            <ul>
                                                {#each pubItem.items as publication}
                                                    <li>{@html publication}</li>
                                                {/each}
                                            </ul>
                                        {:else}
                                            <p>{@html pubItem.items}</p>
                                        {/if}
                                    </div>
                                {/each}
                            {/if}
                        </div>
                    </div>
                </section>
            </div>
        {:else}
            <div class="content">
                <div class="no-results-container">
                    <img src="/images/icons/no-results.png" alt="no results">
                    <h3>Sorry, this project doesn't exist.</h3>
                </div>
            </div>
        {/if}
    </div>
{/await}





<style lang="sass">

@import "../consts.sass"
@include page-header()
@include loading-spinner()
@include no-search-results-text()

.content
    padding: 2em
    box-sizing: border-box

    @media only screen and (min-width: 1200px) 
        padding-right: 10vw
        padding-left: 10vw

    .flex-wrapper
        margin-top: 3rem
        display: flex
        flex-direction: row
        justify-content: flex-start

        ul.card-list
            list-style-type: none
            margin: 0
            margin-top: 1.5rem

            li
                border-radius: 0.3rem
                border: 1px solid rgba(0,0,0,0.2)
                font-size: 1rem
                padding: 0.4rem 0.7rem
                box-sizing: border-box
                margin-top: 0.5rem

        .image-container
            display: flex
            flex-direction: row
            flex-flow: wrap
            margin-top: 2em

            img
                height: 6em
                margin-right: 3em
                margin-bottom: 2.5em
                border-radius: 0.2em

        img.project-picture
            border-radius: 0.2rem

        @media only screen and (max-width: 1200px) 
            flex-direction: column
            flex-flow: wrap

            img.project-picture
                width: 22em

            .flex-column.secondary
                margin-top: 2em
        
        @media only screen and (min-width: 1200px) 
            img.project-picture
                max-width: 30em
            
            .flex-column.secondary
                margin-left: 5em
    

</style>